import { useParams, useSearchParams } from 'react-router-dom';

import { SubsetForm } from '../components/subset-form';
import { useGetDocIdsByQueryKey } from '@/api/queries/useGetDocIdsByQueryKey';
import { useGetDocIds } from '@/api/queries/useGetDocs';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSuggestion } from '@/api/queries/useGetSuggestion';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';

export function SubsetCreator() {
  const { matterId } = useParams();
  const [searchParams] = useSearchParams();
  const suggestionId = searchParams.get('suggestionId');
  const documentIdsParam = searchParams.get('documentIds');
  const folderId = searchParams.get('folderId');
  const includeFolderDedup = searchParams.get('includeFolderDedup') === '1';
  const factsQueryKey = searchParams.get('factsQueryKey');

  if (!matterId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const matterBreadcrumbHead = useMatterBreadcrumbHead(matterId, matter?.name, false);
  const { data: suggestion, isLoading: isLoadingSuggestion } = useGetSuggestion(suggestionId);

  const { data: folderDocIdsData, isLoading: isLoadingFolderDocs } = useGetDocIds({
    matterId,
    subsetId: matter?.allDocumentsCaseId ?? '',
    filters: {
      folderId: folderId ?? undefined,
      includeFolderDeduplicatedDocs: includeFolderDedup ? true : undefined,
    },
    isFlattened: true,
    enabled: !!folderId && !!matter?.allDocumentsCaseId,
  });

  const { data: factsDocIdsData, isLoading: isLoadingFactsDocs } = useGetDocIdsByQueryKey(matterId, factsQueryKey);

  // Prepare initial data from suggestion, documentIds param, folderId, or factsQueryKey
  const initialData = suggestion?.docIds
    ? {
        name: suggestion.name,
        issues: [],
        selectedDocIds: suggestion.docIds,
      }
    : documentIdsParam
      ? {
          name: '',
          issues: [],
          selectedDocIds: documentIdsParam.split(',').filter(Boolean),
        }
      : folderId && folderDocIdsData?.docIds
        ? {
            name: '',
            issues: [],
            selectedDocIds: folderDocIdsData.docIds,
          }
        : factsQueryKey && factsDocIdsData?.docIds
          ? {
              name: '',
              issues: [],
              selectedDocIds: factsDocIdsData.docIds,
            }
          : undefined;

  const isLoadingInitial =
    (!!suggestionId && (isLoadingSuggestion || !suggestion)) ||
    (!!folderId && isLoadingFolderDocs) ||
    (!!factsQueryKey && isLoadingFactsDocs);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          ...matterBreadcrumbHead,
          { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
          { href: `/matters/${matterId}/subsets/new`, label: 'New', active: true },
        ]}
      />

      <SubsetForm
        key={suggestion?.id || (suggestionId && 'loading') || documentIdsParam || folderId || factsQueryKey || 'create'}
        mode="create"
        matterId={matterId}
        initialData={initialData}
        suggestionId={suggestionId || undefined}
        isLoadingInitialData={isLoadingInitial}
        showIncludedByDefault={!!folderId || !!suggestionId || !!factsQueryKey || !!documentIdsParam}
        initialServerFilters={
          folderId ? { folderId, includeFolderDeduplicatedDocs: includeFolderDedup ? true : undefined } : undefined
        }
      />
    </div>
  );
}
